import { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import { strategiesApi } from '../../api/strategies';
import InfoBubble from '../../components/common/InfoBubble';

// ── Theme ──────────────────────────────────────────────────
const C = {
  navy: '#1B2A4A', navyMuted: '#3A5080',
  burgundy: '#7A2E3B', burgundyMuted: 'rgba(122,46,59,0.12)',
  gold: '#B8953E', goldMuted: 'rgba(184,149,62,0.15)',
  hunter: '#2D5F4A', hunterMuted: 'rgba(45,95,74,0.12)',
  ivory: '#FAF7F2', cream: '#F3EDE4', parchment: '#EBE3D6',
  ink: '#1A1A1A', textBody: '#3D3830', textMuted: '#7A7265', textLight: '#A69D91',
  border: '#D6CCBE', borderLight: '#E8E0D4',
};

function fmt(v: number | null | undefined): string {
  if (v == null) return '—';
  const abs = Math.abs(v);
  const neg = v < 0;
  if (abs >= 1_000_000) return `${neg ? '(' : ''}$${(abs / 1_000_000).toFixed(1)}M${neg ? ')' : ''}`;
  if (abs >= 1000) return `${neg ? '(' : ''}$${(abs / 1000).toFixed(0)}K${neg ? ')' : ''}`;
  return `${neg ? '(' : ''}$${abs.toFixed(0)}${neg ? ')' : ''}`;
}

// Strategy type to InfoBubble term mapping
const strategyInfoTerm: Record<string, string> = {
  grat: 'GRAT', idgt: 'IDGT', annual_gifting: 'Annual Gifting', ilit: 'ILIT',
};

// ── Shared Components ──────────────────────────────────────

function StatCard({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div className="flex-1 min-w-[140px] px-5 py-4 border-r border-stone-200 last:border-r-0">
      <p className="text-[10px] font-sans font-bold text-stone-400 tracking-widest uppercase mb-2">{label}</p>
      <p className="text-2xl font-display font-semibold leading-none" style={{ color: accent || C.ink }}>{value}</p>
      {sub && <p className="text-[11px] font-sans text-stone-500 mt-1.5">{sub}</p>}
    </div>
  );
}

function SectionTitle({ children, sub }: { children: React.ReactNode; sub?: string }) {
  return (
    <div className="mb-4">
      <h3 className="text-[13px] font-sans font-bold text-stone-500 tracking-widest uppercase border-b-2 border-navy-800 inline-block pb-1">{children}</h3>
      {sub && <p className="text-[13px] font-body text-stone-400 italic mt-1.5">{sub}</p>}
    </div>
  );
}

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`bg-ivory-50 border border-stone-300 rounded-sm ${className}`}>{children}</div>;
}

function AlertBox({ type, title, children, info }: { type: 'danger' | 'warning' | 'success' | 'info'; title?: string; children: React.ReactNode; info?: string }) {
  const styles = {
    danger: { bg: 'bg-red-50', border: 'border-l-red-800', tc: 'text-red-800' },
    warning: { bg: 'bg-amber-50', border: 'border-l-amber-600', tc: 'text-amber-700' },
    success: { bg: 'bg-emerald-50', border: 'border-l-emerald-700', tc: 'text-emerald-700' },
    info: { bg: 'bg-blue-50', border: 'border-l-blue-800', tc: 'text-blue-800' },
  };
  const s = styles[type];
  return (
    <div className={`${s.bg} ${s.border} border-l-[3px] rounded-sm p-3.5 mb-2.5`}>
      {title && <p className={`font-sans text-[13px] font-bold ${s.tc} mb-1 inline-flex items-center`}>{title}{info && <InfoBubble term={info} />}</p>}
      <div className="font-body text-[12px] text-stone-700 leading-relaxed">{children}</div>
    </div>
  );
}

function StatusPill({ status, label }: { status: string; label: string }) {
  const map: Record<string, string> = {
    implemented: 'bg-emerald-100 text-emerald-800', approved: 'bg-emerald-100 text-emerald-800',
    recommended: 'bg-blue-100 text-blue-800', proposed: 'bg-amber-100 text-amber-800',
    modeling: 'bg-amber-100 text-amber-800', rejected: 'bg-red-100 text-red-800',
    none: 'bg-emerald-100 text-emerald-800', low: 'bg-amber-100 text-amber-800',
    medium: 'bg-red-100 text-red-800', high: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-block px-2.5 py-0.5 rounded-sm text-[10px] font-sans font-bold tracking-wide uppercase ${map[status] || map.proposed}`}>
      {label}
    </span>
  );
}

const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload) return null;
  return (
    <div className="bg-ivory-50 border border-stone-300 rounded-sm px-3.5 py-2.5 shadow-lg">
      <p className="text-[11px] font-sans font-semibold text-stone-400 tracking-wide mb-1">{label}</p>
      {payload.map((e: any, i: number) => (
        <p key={i} className="text-[12px] font-sans font-medium" style={{ color: e.color || C.ink }}>
          {e.name}: {fmt(e.value)}
        </p>
      ))}
    </div>
  );
};

// ── Strategy Type Configs ──────────────────────────────────

const STRATEGY_TYPES = [
  { value: 'grat', label: 'Zeroed-Out GRAT', desc: 'Transfer appreciating assets. Annuity returns to grantor. Remainder passes tax-free.' },
  { value: 'idgt', label: 'IDGT Sale', desc: 'Sell to intentionally defective grantor trust for installment note at AFR.' },
  { value: 'annual_gifting', label: 'Annual Exclusion Gifting', desc: 'Transfer using annual exclusion. No exemption consumed.' },
  { value: 'ilit', label: 'ILIT (Life Insurance)', desc: 'Death benefit outside estate provides estate tax liquidity.' },
];

function getDefaultParams(type: string): any {
  switch (type) {
    case 'grat': return { initial_funding: 5000000, term_years: 12, growth_assumption: 0.07 };
    case 'idgt': return { sale_amount: 8000000, note_term: 15, seed_gift_pct: 0.10, growth_assumption: 0.07, grantor_tax_rate: 0.37 };
    case 'annual_gifting': return { per_donee_amount: 19000, num_donees: 12, years: 6, spouse_splitting: true };
    case 'ilit': return { death_benefit: 5000000, annual_premium: 85000, crummey_beneficiaries: 6, policy_type: 'second_to_die', acquire_asset: false, asset_value: 0, asset_growth_rate: 0.07, installment_term: 15 };
    default: return {};
  }
}

// ── Create Strategy Form ───────────────────────────────────

function CreateStrategyForm({ onCreated }: { onCreated: () => void }) {
  const [stratType, setStratType] = useState('grat');
  const [name, setName] = useState('');
  const [params, setParams] = useState<any>(getDefaultParams('grat'));
  const [preview, setPreview] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  const updateParam = (key: string, value: any) => {
    setParams((p: any) => ({ ...p, [key]: value }));
    setPreview(null);
  };

  const handleTypeChange = (type: string) => {
    setStratType(type);
    setParams(getDefaultParams(type));
    setPreview(null);
    setName('');
  };

  const handlePreview = async () => {
    try {
      const res = await strategiesApi.calculate(stratType, params);
      setPreview(res.data.data);
    } catch (err) { console.error(err); }
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await strategiesApi.create({ name, strategyType: stratType, parameters: params });
      setPreview(null);
      setName('');
      setParams(getDefaultParams(stratType));
      onCreated();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  return (
    <div>
      <div className="grid grid-cols-2 gap-5">
        <Card className="p-6">
          <SectionTitle>Strategy Type</SectionTitle>
          <div className="space-y-2 mb-5">
            {STRATEGY_TYPES.map((t) => (
              <div
                key={t.value}
                onClick={() => handleTypeChange(t.value)}
                className={`p-3 rounded-sm border cursor-pointer transition-all ${
                  stratType === t.value ? 'border-navy-800 bg-blue-50' : 'border-stone-200 bg-ivory-200 hover:border-stone-400'
                }`}
              >
                <p className="text-[13px] font-sans font-bold text-stone-800 inline-flex items-center">{t.label}<InfoBubble term={strategyInfoTerm[t.value] || t.label} /></p>
                <p className="text-[11px] font-sans text-stone-500 mt-0.5">{t.desc}</p>
              </div>
            ))}
          </div>

          <SectionTitle>Parameters</SectionTitle>
          {stratType === 'grat' && (
            <div className="space-y-3">
              <ParamInput label="Initial Funding ($)" value={params.initial_funding} onChange={(v) => updateParam('initial_funding', Number(v))} />
              <ParamInput label="Term (years)" value={params.term_years} onChange={(v) => updateParam('term_years', Number(v))} />
              <ParamInput label="Growth Assumption (%)" value={(params.growth_assumption * 100).toFixed(1)} onChange={(v) => updateParam('growth_assumption', Number(v) / 100)} />
            </div>
          )}
          {stratType === 'idgt' && (
            <div className="space-y-3">
              <ParamInput label="Sale Amount ($)" value={params.sale_amount} onChange={(v) => updateParam('sale_amount', Number(v))} />
              <ParamInput label="Note Term (years)" value={params.note_term} onChange={(v) => updateParam('note_term', Number(v))} />
              <ParamInput label="Seed Gift (%)" value={(params.seed_gift_pct * 100).toFixed(0)} onChange={(v) => updateParam('seed_gift_pct', Number(v) / 100)} />
              <ParamInput label="Growth Assumption (%)" value={(params.growth_assumption * 100).toFixed(1)} onChange={(v) => updateParam('growth_assumption', Number(v) / 100)} />
              <ParamInput label="Grantor Tax Rate (%)" value={(params.grantor_tax_rate * 100).toFixed(0)} onChange={(v) => updateParam('grantor_tax_rate', Number(v) / 100)} />
            </div>
          )}
          {stratType === 'annual_gifting' && (
            <div className="space-y-3">
              <ParamInput label="Per Donee Amount ($)" value={params.per_donee_amount} onChange={(v) => updateParam('per_donee_amount', Number(v))} />
              <ParamInput label="Number of Donees" value={params.num_donees} onChange={(v) => updateParam('num_donees', Number(v))} />
              <ParamInput label="Years" value={params.years} onChange={(v) => updateParam('years', Number(v))} />
            </div>
          )}
          {stratType === 'ilit' && (
            <div className="space-y-3">
              <ParamInput label="Death Benefit ($)" value={params.death_benefit} onChange={(v) => updateParam('death_benefit', Number(v))} />
              <ParamInput label="Annual Premium ($)" value={params.annual_premium} onChange={(v) => updateParam('annual_premium', Number(v))} />
              <ParamInput label="Crummey Beneficiaries" value={params.crummey_beneficiaries} onChange={(v) => updateParam('crummey_beneficiaries', Number(v))} />

              <div className="border-t border-stone-300 pt-3 mt-3">
                <label className="flex items-center gap-2 text-[11px] font-sans font-bold text-stone-500 tracking-wide uppercase mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={params.acquire_asset || false}
                    onChange={(e) => updateParam('acquire_asset', e.target.checked)}
                    className="w-4 h-4 rounded border-stone-300 text-navy-600 focus:ring-navy-300"
                  />
                  ILIT Asset Acquisition
                </label>
                {params.acquire_asset && (
                  <div className="space-y-3 pl-1">
                    <p className="text-[11px] font-sans text-stone-400 italic -mt-1 mb-2">
                      Model the ILIT purchasing an asset from the grantor via installment note at AFR
                    </p>
                    <ParamInput label="Asset Value ($)" value={params.asset_value} onChange={(v) => updateParam('asset_value', Number(v))} />
                    <ParamInput label="Asset Growth Rate (%)" value={((params.asset_growth_rate || 0.07) * 100).toFixed(1)} onChange={(v) => updateParam('asset_growth_rate', Number(v) / 100)} />
                    <ParamInput label="Installment Term (years)" value={params.installment_term} onChange={(v) => updateParam('installment_term', Number(v))} />
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-5">
            <button onClick={handlePreview} className="px-4 py-2 rounded-sm text-[12px] font-sans font-bold border border-stone-300 text-stone-700 bg-ivory-200 hover:bg-stone-200 transition-colors">
              Preview Calculation
            </button>
          </div>
        </Card>

        <Card className="p-6">
          <SectionTitle>Calculation Preview</SectionTitle>
          {preview ? (
            <div>
              <div className="grid grid-cols-2 gap-3 mb-5">
                {[
                  { l: 'Estate Reduction', v: fmt(preview.estateReduction), c: C.hunter },
                  { l: 'Tax Savings', v: fmt(preview.projectedTaxSavings), c: C.hunter },
                  { l: 'Annual Cost', v: fmt(preview.annualCost), c: C.burgundy },
                  { l: 'Annual Income', v: fmt(preview.annualIncome), c: C.navy },
                  { l: 'Net Liquidity/yr', v: fmt(preview.netAnnualLiquidity), c: preview.netAnnualLiquidity >= 0 ? C.hunter : C.burgundy },
                  { l: 'Exemption Used', v: fmt(preview.exemptionUsed), c: C.gold },
                ].map((item, i) => (
                  <div key={i} className="px-3 py-2.5 bg-ivory-200 rounded-sm">
                    <p className="text-[10px] font-sans text-stone-400 tracking-wide uppercase mb-1">{item.l}</p>
                    <p className="text-[16px] font-mono font-semibold" style={{ color: item.c }}>{item.v}</p>
                  </div>
                ))}
              </div>

              <div className="p-3 bg-ivory-200 rounded-sm border-l-[3px] mb-4" style={{ borderLeftColor: C.navy }}>
                <p className="text-[12px] font-body text-stone-600 leading-relaxed">{preview.notes}</p>
              </div>

              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="Strategy name (e.g., 'Zeroed-Out GRAT — RE Portfolio')"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="flex-1 px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-sans text-stone-800 focus:outline-none focus:border-navy-600"
                />
                <button
                  onClick={handleSave}
                  disabled={!name.trim() || saving}
                  className="px-5 py-2 rounded-sm text-[12px] font-sans font-bold text-ivory-100 disabled:opacity-50 transition-colors"
                  style={{ background: C.navy }}
                >
                  {saving ? 'Saving...' : 'Save Strategy'}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="text-stone-300 text-4xl mb-3">⚖</div>
              <p className="text-[14px] font-sans text-stone-500 mb-1">Select a strategy type and configure parameters</p>
              <p className="text-[12px] font-sans text-stone-400">Click "Preview Calculation" to see projected results</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function ParamInput({ label, value, onChange }: { label: string; value: any; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-1">{label}</label>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-mono text-stone-800 focus:outline-none focus:border-navy-600"
      />
    </div>
  );
}

// ── Combination Engine Panel ───────────────────────────────

function CombinationPanel({ strategies }: { strategies: any[] }) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [comboResult, setComboResult] = useState<any>(null);
  const [running, setRunning] = useState(false);

  const toggleStrategy = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    setComboResult(null);
  };

  const runCombination = async () => {
    if (selectedIds.length === 0) return;
    setRunning(true);
    try {
      const res = await strategiesApi.runCombination(selectedIds);
      setComboResult(res.data.data);
    } catch (err) { console.error(err); }
    setRunning(false);
  };

  return (
    <div>
      <div className="grid grid-cols-2 gap-5">
        <Card className="p-6">
          <SectionTitle sub="Select strategies to layer together">Select Strategies</SectionTitle>
          {strategies.length === 0 ? (
            <p className="text-[13px] font-sans text-stone-400">No strategies created yet. Create strategies first.</p>
          ) : (
            <div className="space-y-2 mb-4">
              {strategies.map((s: any) => (
                <div
                  key={s.id}
                  onClick={() => toggleStrategy(s.id)}
                  className={`flex items-center justify-between p-3 rounded-sm border cursor-pointer transition-all ${
                    selectedIds.includes(s.id) ? 'border-navy-800 bg-blue-50' : 'border-stone-200 bg-ivory-200'
                  }`}
                >
                  <div>
                    <p className="text-[13px] font-sans font-bold text-stone-800">{s.name}</p>
                    <p className="text-[11px] font-sans text-stone-500">{s.strategy_type.toUpperCase()} · Savings: {fmt(parseFloat(s.projected_tax_savings || '0'))}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-sm border-2 flex items-center justify-center ${selectedIds.includes(s.id) ? 'border-navy-800 bg-navy-800' : 'border-stone-300'}`}>
                    {selectedIds.includes(s.id) && <span className="text-white text-[11px] font-bold">✓</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
          <button
            onClick={runCombination}
            disabled={selectedIds.length === 0 || running}
            className="w-full px-4 py-2.5 rounded-sm text-[12px] font-sans font-bold text-ivory-100 disabled:opacity-50 transition-colors"
            style={{ background: C.navy }}
          >
            {running ? 'Calculating...' : `Run Combination (${selectedIds.length} selected)`}
          </button>
        </Card>

        <Card className="p-6">
          {comboResult ? (
            <div>
              <SectionTitle sub="Progressive tax reduction as strategies layer">Combined Impact</SectionTitle>

              <div className="grid grid-cols-2 gap-3 mb-5">
                {[
                  { l: 'Combined Savings', v: fmt(comboResult.combined.totalTaxSavings), c: C.hunter },
                  { l: 'Estate Reduction', v: fmt(comboResult.combined.totalEstateReduction), c: C.navy },
                  { l: 'Net Liquidity/yr', v: fmt(comboResult.combined.netAnnualLiquidity), c: comboResult.combined.netAnnualLiquidity >= 0 ? C.hunter : C.gold },
                  { l: 'Revised Tax', v: fmt(comboResult.combined.revisedFederalTax), c: C.burgundy },
                ].map((item, i) => (
                  <div key={i} className="px-3 py-2.5 bg-ivory-200 rounded-sm">
                    <p className="text-[10px] font-sans text-stone-400 tracking-wide uppercase mb-1">{item.l}</p>
                    <p className="text-[16px] font-mono font-semibold" style={{ color: item.c }}>{item.v}</p>
                  </div>
                ))}
              </div>

              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={comboResult.progressiveSteps} layout="vertical" barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.borderLight} horizontal={false} />
                  <XAxis type="number" tick={{ fill: C.textLight, fontSize: 11 }} axisLine={{ stroke: C.border }} tickFormatter={(v: number) => fmt(v)} />
                  <YAxis type="category" dataKey="label" tick={{ fill: C.textMuted, fontSize: 10 }} axisLine={{ stroke: C.border }} width={110} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="federalTax" stackId="a" fill={C.burgundy} name="Federal Tax" barSize={16} fillOpacity={0.8} />
                  <Bar dataKey="stateTax" stackId="a" fill={C.navyMuted} name="State Tax" barSize={16} fillOpacity={0.8} radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="text-stone-300 text-4xl mb-3">📊</div>
              <p className="text-[14px] font-sans text-stone-500 mb-1">Select strategies and run the combination engine</p>
              <p className="text-[12px] font-sans text-stone-400">See how layering strategies progressively reduces the tax bill</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}


// ── Gifting Optimizer Panel ────────────────────────────────

function GiftingOptimizerPanel() {
  const [numDonees, setNumDonees] = useState(12);
  const [years, setYears] = useState(6);
  const [spouseSplit, setSpouseSplit] = useState(true);
  const [result, setResult] = useState<any>(null);
  const [running, setRunning] = useState(false);

  const runOptimizer = async () => {
    setRunning(true);
    try {
      const res = await strategiesApi.giftingOptimizer({
        numDonees, years, includeSpouseSplitting: spouseSplit,
      });
      setResult(res.data.data);
    } catch (err) { console.error(err); }
    setRunning(false);
  };

  const recColors: Record<string, { bg: string; text: string; label: string }> = {
    strong: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Strong' },
    moderate: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Moderate' },
    weak: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Weak' },
    avoid: { bg: 'bg-red-100', text: 'text-red-800', label: 'Avoid' },
  };

  return (
    <div>
      <div className="grid grid-cols-3 gap-5">
        {/* Config Panel */}
        <Card className="p-6">
          <SectionTitle sub="Configure annual exclusion gifting parameters">Gifting Parameters</SectionTitle>
          <div className="space-y-3">
            <ParamInput label="Number of Donees" value={numDonees} onChange={(v) => setNumDonees(Number(v))} />
            <ParamInput label="Years" value={years} onChange={(v) => setYears(Number(v))} />
            <div className="mt-2">
              <label className="flex items-center gap-2 text-[11px] font-sans font-bold text-stone-500 tracking-wide uppercase cursor-pointer">
                <input type="checkbox" checked={spouseSplit} onChange={(e) => setSpouseSplit(e.target.checked)} className="w-4 h-4 rounded border-stone-300 text-navy-600" />
                Gift Splitting with Spouse
              </label>
            </div>
          </div>

          {result && (
            <div className="mt-5 p-3 bg-ivory-200 rounded-sm">
              <p className="text-[10px] font-sans text-stone-400 tracking-wide uppercase mb-2">Gifting Capacity</p>
              <p className="text-[16px] font-mono font-semibold" style={{ color: C.navy }}>{fmt(result.annualCapacity)}<span className="text-[11px] font-sans text-stone-400 ml-1">/ year</span></p>
              <p className="text-[16px] font-mono font-semibold mt-1" style={{ color: C.hunter }}>{fmt(result.totalCapacity)}<span className="text-[11px] font-sans text-stone-400 ml-1">total over {years} years</span></p>
              <p className="text-[11px] font-sans text-stone-400 mt-2">${(result.annualExclusion / 1000).toFixed(0)}K exclusion × {numDonees} donees{spouseSplit ? ' × 2 (split)' : ''}</p>
            </div>
          )}

          <button
            onClick={runOptimizer}
            disabled={running}
            className="w-full mt-4 px-4 py-2.5 rounded-sm text-[12px] font-sans font-bold text-ivory-100 disabled:opacity-50 transition-colors"
            style={{ background: C.navy }}
          >
            {running ? 'Analyzing...' : 'Rank Assets for Gifting'}
          </button>
        </Card>

        {/* Results */}
        <div className="col-span-2">
          {result ? (
            <Card className="overflow-hidden">
              <div className="px-6 pt-5 pb-3">
                <SectionTitle sub="Assets ranked by gifting suitability — highest score = best candidate">Asset Gifting Recommendations</SectionTitle>
              </div>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-ivory-200">
                    {['Asset', 'FMV', 'Basis Ratio', 'Growth', 'Discount', 'Score', 'Rating'].map((h) => (
                      <th key={h} className="text-[10px] font-sans font-bold text-stone-400 tracking-widest uppercase text-left px-3.5 py-2.5 border-b-2 border-stone-300">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.rankedAssets.map((a: any, i: number) => {
                    const rc = recColors[a.recommendation] || recColors.weak;
                    return (
                      <tr key={a.assetId} className={i % 2 === 0 ? '' : 'bg-ivory-200'}>
                        <td className="px-3.5 py-3 border-b border-stone-200">
                          <p className="text-[13px] font-sans font-semibold text-stone-800">{a.name}</p>
                          <p className="text-[10px] font-sans text-stone-400 uppercase">{a.assetType.replace(/_/g, ' ')}</p>
                        </td>
                        <td className="px-3.5 py-3 text-[13px] font-mono border-b border-stone-200" style={{ color: C.navy }}>{fmt(a.fmv)}</td>
                        <td className="px-3.5 py-3 text-[13px] font-mono border-b border-stone-200" style={{ color: a.basisRatio >= 70 ? C.hunter : a.basisRatio < 30 ? C.burgundy : C.textBody }}>{a.basisRatio}%</td>
                        <td className="px-3.5 py-3 text-[13px] font-mono border-b border-stone-200">{a.appreciationRate}%</td>
                        <td className="px-3.5 py-3 text-[13px] font-mono border-b border-stone-200">{a.discountPct > 0 ? `${a.discountPct}%` : '—'}</td>
                        <td className="px-3.5 py-3 text-[15px] font-mono font-bold border-b border-stone-200" style={{ color: a.score >= 50 ? C.hunter : a.score >= 30 ? C.navy : C.textMuted }}>{a.score}</td>
                        <td className="px-3.5 py-3 border-b border-stone-200">
                          <span className={`inline-block px-2.5 py-0.5 rounded-sm text-[10px] font-sans font-bold tracking-wide uppercase ${rc.bg} ${rc.text}`}>{rc.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Reasoning cards */}
              {result.topRecommendations.length > 0 && (
                <div className="px-6 py-4 border-t border-stone-200">
                  <p className="text-[10px] font-sans font-bold text-stone-400 tracking-widest uppercase mb-3">Top Recommendations — Reasoning</p>
                  {result.topRecommendations.map((a: any) => (
                    <div key={a.assetId} className="p-3 bg-ivory-200 rounded-sm border-l-[3px] mb-2" style={{ borderLeftColor: a.recommendation === 'strong' ? C.hunter : C.navy }}>
                      <p className="text-[12px] font-sans font-bold mb-1" style={{ color: a.recommendation === 'strong' ? C.hunter : C.navy }}>{a.name} — Score: {a.score}</p>
                      {a.reasons.map((r: string, j: number) => (
                        <p key={j} className="text-[11px] font-body text-stone-600 leading-relaxed">• {r}</p>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {result.avoidList.length > 0 && (
                <div className="px-6 py-4 border-t border-stone-200">
                  <p className="text-[10px] font-sans font-bold text-red-500 tracking-widest uppercase mb-3">Assets to Avoid Gifting</p>
                  {result.avoidList.map((a: any) => (
                    <div key={a.assetId} className="p-3 bg-red-50 rounded-sm border-l-[3px] border-l-red-300 mb-2">
                      <p className="text-[12px] font-sans font-bold text-red-800 mb-1">{a.name}</p>
                      {a.reasons.map((r: string, j: number) => (
                        <p key={j} className="text-[11px] font-body text-red-700 leading-relaxed">• {r}</p>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ) : (
            <Card className="p-12 text-center">
              <div className="text-stone-300 text-4xl mb-3">🎯</div>
              <p className="text-[14px] font-sans text-stone-500 mb-1">Configure gifting parameters and run the optimizer</p>
              <p className="text-[12px] font-sans text-stone-400">The optimizer ranks your assets by gifting suitability based on growth rate, basis ratio, valuation discounts, yield, and liquidity</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────

export default function StrategyWorkbenchPage() {
  const [panel, setPanel] = useState<'active' | 'create' | 'combination' | 'gifting-optimizer'>('active');
  const [strategies, setStrategies] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const [listRes, summaryRes] = await Promise.all([
        strategiesApi.list(),
        strategiesApi.summary().catch(() => ({ data: { data: null } })),
      ]);
      setStrategies(listRes.data.data || []);
      setSummary(summaryRes.data.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const handleStatusChange = async (id: string, status: string) => {
    try {
      await strategiesApi.updateStatus(id, status);
      loadData();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this strategy?')) return;
    try {
      await strategiesApi.delete(id);
      loadData();
    } catch (err) { console.error(err); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[14px] font-sans text-stone-400 animate-pulse">Loading strategy workbench...</p>
      </div>
    );
  }

  const totalSavings = parseFloat(summary?.total_savings || '0');
  const totalCost = parseFloat(summary?.total_annual_cost || '0');
  const totalIncome = parseFloat(summary?.total_annual_income || '0');

  const panels = [
    { id: 'active' as const, label: 'Active Strategies' },
    { id: 'create' as const, label: 'Create New' },
    { id: 'combination' as const, label: 'Combination Engine' },
    { id: 'gifting-optimizer' as const, label: 'Gifting Optimizer' },
  ];

  return (
    <div>
      {/* KPI Bar */}
      <Card className="flex mb-6 overflow-hidden flex-wrap">
        <StatCard label="Combined Savings" value={fmt(totalSavings)} sub={`${summary?.total_strategies || 0} strategies`} accent={C.hunter} />
        <StatCard label="Net Liquidity Impact" value={fmt(totalIncome - totalCost)} sub={totalIncome > totalCost ? 'Net positive' : 'Net cost'} accent={C.navy} />
        <StatCard label="Annual Cost" value={fmt(totalCost)} sub="Premiums + gifts" accent={C.gold} />
        <StatCard label="Annual Income" value={fmt(totalIncome)} sub="Note payments + annuities" accent={C.ink} />
      </Card>

      {/* Panel Tabs */}
      <div className="flex gap-1 mb-5 bg-stone-200 rounded-sm p-0.5 w-fit">
        {panels.map((p) => (
          <button
            key={p.id}
            onClick={() => setPanel(p.id)}
            className={`px-4 py-1.5 rounded-sm border-none cursor-pointer text-[11px] font-sans font-semibold transition-all duration-150 ${
              panel === p.id ? 'text-ivory-100' : 'text-stone-500 bg-transparent hover:text-stone-700'
            }`}
            style={panel === p.id ? { background: C.navy, color: C.ivory } : {}}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Active Strategies */}
      {panel === 'active' && (
        <div>
          {strategies.length === 0 ? (
            <Card className="p-12 text-center">
              <div className="text-stone-300 text-4xl mb-3">⚖</div>
              <p className="text-[14px] font-sans text-stone-500 mb-1">No strategies created yet</p>
              <p className="text-[12px] font-sans text-stone-400 mb-4">Click "Create New" to model your first estate tax reduction strategy</p>
              <button onClick={() => setPanel('create')} className="px-5 py-2 rounded-sm text-[12px] font-sans font-bold text-ivory-100" style={{ background: C.navy }}>
                Create First Strategy
              </button>
            </Card>
          ) : (
            <div>
              <Card className="overflow-hidden mb-5">
                <div className="px-6 pt-5 pb-3">
                  <SectionTitle sub="Individual strategies modeled against current family office position">Active Strategies</SectionTitle>
                </div>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-ivory-200">
                      {['Strategy', 'Type', 'Estate Reduction', 'Tax Savings', 'Annual Cost', 'Risk', 'Status', 'Actions'].map((h) => (
                        <th key={h} className="text-[10px] font-sans font-bold text-stone-400 tracking-widest uppercase text-left px-3.5 py-2.5 border-b-2 border-stone-300">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {strategies.map((s: any, i: number) => (
                      <tr key={s.id} className={i % 2 === 0 ? '' : 'bg-ivory-200'}>
                        <td className="px-3.5 py-3 text-[13px] font-sans font-semibold text-stone-800 border-b border-stone-200">{s.name}</td>
                        <td className="px-3.5 py-3 text-[13px] font-sans text-stone-600 border-b border-stone-200">
                          <span className="inline-flex items-center uppercase">{s.strategy_type}<InfoBubble term={strategyInfoTerm[s.strategy_type] || s.strategy_type} /></span>
                        </td>
                        <td className="px-3.5 py-3 text-[13px] font-mono border-b border-stone-200" style={{ color: C.navy }}>{fmt(parseFloat(s.projected_transfer || '0'))}</td>
                        <td className="px-3.5 py-3 text-[13px] font-mono border-b border-stone-200" style={{ color: C.hunter }}>{fmt(parseFloat(s.projected_tax_savings || '0'))}</td>
                        <td className="px-3.5 py-3 text-[13px] font-mono border-b border-stone-200">{fmt(parseFloat(s.annual_cost || '0'))}</td>
                        <td className="px-3.5 py-3 border-b border-stone-200">
                          <StatusPill status={s.risk_level || 'low'} label={s.risk_level || 'Low'} />
                        </td>
                        <td className="px-3.5 py-3 border-b border-stone-200">
                          <select
                            value={s.implementation_status}
                            onChange={(e) => handleStatusChange(s.id, e.target.value)}
                            className="text-[11px] font-sans font-bold bg-transparent border border-stone-300 rounded-sm px-2 py-1 text-stone-600 cursor-pointer"
                          >
                            {['proposed', 'modeling', 'recommended', 'approved', 'implemented', 'rejected'].map((st) => (
                              <option key={st} value={st}>{st.charAt(0).toUpperCase() + st.slice(1)}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3.5 py-3 border-b border-stone-200">
                          <button onClick={() => handleDelete(s.id)} className="text-[11px] font-sans text-red-600 hover:text-red-800">Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>

              {/* Strategy Notes */}
              <div className="grid grid-cols-2 gap-5">
                <Card className="p-6">
                  <SectionTitle>Strategy Notes</SectionTitle>
                  {strategies.map((s: any) => {
                    const notes = s.results_cache?.notes || '';
                    const colors: Record<string, string> = { grat: C.navy, idgt: C.burgundy, annual_gifting: C.hunter, ilit: C.gold };
                    return (
                      <div key={s.id} className="p-3 bg-ivory-200 rounded-sm border-l-[3px] mb-2" style={{ borderLeftColor: colors[s.strategy_type] || C.navy }}>
                        <p className="text-[12px] font-sans font-bold mb-1 inline-flex items-center" style={{ color: colors[s.strategy_type] || C.navy }}>{s.name}<InfoBubble term={strategyInfoTerm[s.strategy_type] || s.strategy_type} /></p>
                        <p className="text-[12px] font-body text-stone-600 leading-relaxed">{notes}</p>
                      </div>
                    );
                  })}
                </Card>
                <Card className="p-6">
                  <SectionTitle>Planning Guidance</SectionTitle>
                  <AlertBox type="info" title="§7520 Rate" info="§7520">
                    The GRAT annuity is calculated using the IRS §7520 rate (currently 4.8% for March 2026). A lower rate makes GRATs more effective. Recalculate when it changes monthly.
                  </AlertBox>
                  <AlertBox type="warning" title="GRAT Mortality Risk" info="§2036">
                    If the grantor dies during the GRAT term, the trust assets are included in the estate under §2036. Longer terms = more risk. Consider a rolling short-term GRAT series.
                  </AlertBox>
                  <AlertBox type="info" title="IDGT — Grantor Trust Status" info="IDGT">
                    The grantor paying the trust's income tax is an additional tax-free transfer. Ensure the trust maintains its "defective" grantor trust status for the note term.
                  </AlertBox>
                  <AlertBox type="success" title="Annual Exclusion" info="§2503(b)">
                    Annual exclusion gifts ($19K/donee for 2026) consume no lifetime exemption. Include Crummey withdrawal rights for gifts to trusts. Document all Crummey notices.
                  </AlertBox>
                </Card>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create New */}
      {panel === 'create' && (
        <CreateStrategyForm onCreated={() => { loadData(); setPanel('active'); }} />
      )}

      {/* Combination Engine */}
      {panel === 'combination' && (
        <CombinationPanel strategies={strategies} />
      )}

      {/* Gifting Optimizer */}
      {panel === 'gifting-optimizer' && (
        <GiftingOptimizerPanel />
      )}
    </div>
  );
}
